import React, { useRef, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAITaskRegistration } from "../lib/aiTasks";
import {
  FileArrowUp, Robot, CheckCircle, WarningCircle, X,
} from "@phosphor-icons/react";

/**
 * CV import — laat de coachee een bestaand CV (PDF of DOCX) uploaden
 * waarna de AI alle velden in het CV-formulier invult. Toont een
 * bevestigingsmodal omdat het bestaande CV-gegevens overschrijft.
 */
export default function CVImportCard({ coacheeId, onImported }) {
  const fileInputRef = useRef(null);
  const [pendingFile, setPendingFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  useAITaskRegistration({
    active: importing,
    id: coacheeId ? `cv-import-${coacheeId}` : null,
    label: "CV importeren met AI",
    returnTo: "/cv",
  });

  const onPick = () => fileInputRef.current?.click();

  const onFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = ""; // sta toe hetzelfde bestand opnieuw te kiezen
    if (!f) return;
    const nameLower = (f.name || "").toLowerCase();
    if (!nameLower.endsWith(".pdf") && !nameLower.endsWith(".docx")) {
      setError("Alleen PDF of DOCX (Word) bestanden worden ondersteund.");
      return;
    }
    if (f.size > 8 * 1024 * 1024) {
      setError("Bestand is groter dan 8 MB.");
      return;
    }
    setError("");
    setPendingFile(f);
  };

  const onConfirm = async () => {
    if (!pendingFile || !coacheeId) return;
    setError("");
    setImporting(true);
    try {
      const form = new FormData();
      form.append("file", pendingFile);
      const { data } = await api.post(
        `/coachees/${coacheeId}/cv/import`,
        form,
        {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 90000, // CV-parse kan tot ~30s duren; ruime marge
        },
      );
      setResult(data);
      setPendingFile(null);
      if (onImported) onImported();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setImporting(false);
    }
  };

  const onCancel = () => {
    setPendingFile(null);
    setError("");
  };

  return (
    <div className="neuro-card-bold p-5 bg-white mb-6" data-testid="cv-import-card">
      <div className="flex items-start gap-3">
        <div className="shrink-0 inline-flex h-12 w-12 items-center justify-center bg-[#1E88E5]/10 text-[#1E88E5] rounded-sm">
          <FileArrowUp size={26} weight="duotone" />
        </div>
        <div className="flex-1">
          <h2 className="font-display text-lg font-bold tracking-tight">Heb je al een CV?</h2>
          <p className="text-sm text-zinc-600 mt-1 leading-relaxed">
            Upload je bestaande CV als PDF of Word-document. De AI vult dan
            automatisch alle velden hieronder in zodat je niet alles opnieuw
            hoeft te typen — daarna kun je per regel polijsten.
          </p>

          {error && (
            <div className="mt-3 border border-[#E53935]/30 bg-[#E53935]/5 text-[#E53935] text-sm px-3 py-2 rounded-sm flex items-start gap-2" data-testid="cv-import-error">
              <WarningCircle size={16} weight="duotone" className="shrink-0 mt-0.5" />
              <div>{error}</div>
            </div>
          )}

          {result && !pendingFile && (
            <div className="mt-3 border border-[#43A047]/30 bg-[#43A047]/5 text-[#2E7D32] text-sm px-3 py-2 rounded-sm flex items-start gap-2" data-testid="cv-import-success">
              <CheckCircle size={16} weight="bold" className="shrink-0 mt-0.5" />
              <div>
                <strong>{result.filename}</strong> geïmporteerd —
                {" "}{result.work_experience_count} werkervaringen,
                {" "}{result.education_count} opleidingen,
                {" "}{result.skills_count} vaardigheden ingevuld.
              </div>
            </div>
          )}

          <div className="mt-4 flex flex-wrap gap-2 items-center">
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              onChange={onFile}
              className="hidden"
              data-testid="cv-import-file-input"
            />
            <button
              type="button"
              onClick={onPick}
              disabled={importing}
              data-testid="cv-import-pick-btn"
              className="inline-flex items-center gap-2 bg-[#1E88E5] text-white px-4 py-2 text-xs font-semibold tracking-widest uppercase rounded-sm hover:bg-[#1565C0] transition-colors disabled:bg-zinc-300 disabled:cursor-not-allowed"
            >
              <FileArrowUp size={14} weight="bold" /> Kies bestand
            </button>
            <span className="text-xs text-zinc-500">PDF of DOCX · max 8 MB</span>
          </div>
        </div>
      </div>

      {/* Confirmation modal */}
      {pendingFile && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          data-testid="cv-import-confirm-modal"
          role="dialog"
          aria-modal="true"
        >
          <div className="bg-white max-w-md w-full p-6 rounded-sm border-2 border-zinc-900">
            <div className="flex items-start gap-3 mb-3">
              <WarningCircle size={24} weight="duotone" className="text-[#1E88E5] shrink-0 mt-0.5" />
              <h3 className="font-display text-lg font-bold tracking-tight flex-1">
                CV importeren?
              </h3>
              {!importing && (
                <button
                  type="button"
                  onClick={onCancel}
                  data-testid="cv-import-modal-close"
                  className="text-zinc-400 hover:text-zinc-900 transition-colors"
                  aria-label="Sluiten"
                >
                  <X size={20} />
                </button>
              )}
            </div>
            <p className="text-sm text-zinc-700 leading-relaxed mb-3">
              We importeren <strong>{pendingFile.name}</strong> en laten de AI
              er een gestructureerd CV van maken.
            </p>
            <ul className="text-sm text-zinc-700 leading-relaxed space-y-1 mb-4 list-disc pl-5">
              <li><strong>Let op:</strong> bestaande CV-velden worden overschreven.</li>
              <li>Je profielfoto en eventuele AI-feedback blijven behouden.</li>
              <li>Dit duurt ongeveer 15-30 seconden.</li>
            </ul>

            {importing ? (
              <div className="flex items-center gap-3 px-3 py-3 bg-[#1E88E5]/5 border border-[#1E88E5]/20 rounded-sm" data-testid="cv-import-loading">
                <Robot size={22} weight="duotone" className="text-[#1E88E5] animate-pulse" />
                <div className="text-sm text-zinc-700">
                  De AI leest je CV uit en vult de velden in...
                </div>
              </div>
            ) : (
              <div className="flex gap-2 justify-end">
                <button
                  type="button"
                  onClick={onCancel}
                  data-testid="cv-import-cancel"
                  className="px-4 py-2 text-xs font-semibold tracking-widest uppercase rounded-sm border-2 border-zinc-300 hover:border-zinc-500 transition-colors"
                >
                  Annuleer
                </button>
                <button
                  type="button"
                  onClick={onConfirm}
                  data-testid="cv-import-confirm"
                  className="px-4 py-2 text-xs font-semibold tracking-widest uppercase rounded-sm bg-zinc-900 text-white hover:bg-[#1E88E5] transition-colors inline-flex items-center gap-2"
                >
                  <Robot size={14} weight="bold" /> Ja, importeer
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
